import { z } from "zod";
import { containerMetaFieldsSchema, containerMetaStatusSchema, metaOf, overviewCommentSchema, overviewContainerSchema, overviewDocumentSchema, overviewDocumentsSchema, overviewSectionSchema, parseArrayItems, parseContainer, parseNullableContainer, toContainer, toMetaContainer, toSectionsContainer, } from "./shared.js";
// Cost-estimate and damage-appraisal share every schema below — `relevantContainers`,
// `documents`, and open (`Record<string, …>`) namespaces — and only diverge in which specific
// containers are relevant and whether `workshop` exists.
const overviewNamespaceSchema = z.record(z.string(), overviewContainerSchema);
/** `item` / `customer` / `workshop`: a `null` container means the reference is confirmed absent. */
const nullableNamespaceSchema = z.record(z.string(), overviewContainerSchema.nullable());
const metaAwareContainerSchema = overviewContainerSchema.extend(containerMetaFieldsSchema.shape).extend({
    documentCount: z.number(),
    documentCountHasLoadError: z.boolean(),
});
const repairServiceContainerSchema = metaAwareContainerSchema.omit({ data: true }).extend({
    sections: z.array(overviewSectionSchema),
    sectionsHasLoadError: z.boolean(),
});
// Cost-estimate carries all four chip-relevant containers; damage-appraisal omits ContainerEstimate.
const costEstimateRelevantContainersSchema = z.object({
    ContainerEstimate: metaAwareContainerSchema,
    ContainerCommissionedExpertReport: metaAwareContainerSchema,
    ContainerExpertReport: metaAwareContainerSchema,
    ContainerRepairService: repairServiceContainerSchema,
});
const damageAppraisalRelevantContainersSchema = z.object({
    ContainerCommissionedExpertReport: metaAwareContainerSchema,
    ContainerExpertReport: metaAwareContainerSchema,
    ContainerRepairService: repairServiceContainerSchema,
});
const baseSchema = z.object({
    claiminiId: z.string().nullish(),
    overview: overviewNamespaceSchema,
    item: nullableNamespaceSchema,
    customer: nullableNamespaceSchema,
    documents: overviewDocumentsSchema,
});
// cost-estimate includes a workshop namespace; damage-appraisal does not.
export const costEstimateOverviewSchema = baseSchema.extend({
    workflow: z.literal("cost-estimate"),
    workshop: nullableNamespaceSchema,
    relevantContainers: costEstimateRelevantContainersSchema,
});
export const damageAppraisalOverviewSchema = baseSchema.extend({
    workflow: z.literal("damage-appraisal"),
    relevantContainers: damageAppraisalRelevantContainersSchema,
});
const FAILED_META_AWARE_CONTAINER = {
    data: null,
    hasLoadError: true,
    metaStatus: null,
    metaHasLoadError: true,
    documentCount: 0,
    documentCountHasLoadError: true,
    comments: [],
    commentsHasLoadError: true,
};
const FAILED_REPAIR_SERVICE_CONTAINER = {
    hasLoadError: true,
    metaStatus: null,
    metaHasLoadError: true,
    documentCount: 0,
    documentCountHasLoadError: true,
    comments: [],
    commentsHasLoadError: true,
    sections: [],
    sectionsHasLoadError: true,
};
// Loose envelope: validates only the partitioning (each namespace is an object, documents
// is present). Container and document shapes are checked per-entry by the parse functions below,
// so one structurally-drifted container can't reject the whole response. Workshop is
// required for cost-estimate and absent for damage-appraisal — mirroring the strict schema.
const baseEnvelopeSchema = z.object({
    claiminiId: z.string().nullish(),
    overview: z.record(z.string(), z.unknown()),
    item: z.record(z.string(), z.unknown()),
    customer: z.record(z.string(), z.unknown()),
    documents: z.unknown(),
    relevantContainers: z.record(z.string(), z.unknown()),
});
export const costEstimateEnvelopeSchema = baseEnvelopeSchema.extend({
    workflow: z.literal("cost-estimate"),
    workshop: z.record(z.string(), z.unknown()),
});
export const damageAppraisalEnvelopeSchema = baseEnvelopeSchema.extend({
    workflow: z.literal("damage-appraisal"),
});
/** `overview` containers are never null; a malformed one collapses to a load error. */
function parseOverviewNamespace(raw) {
    const out = {};
    for (const [container, value] of Object.entries(raw)) {
        out[container] = parseContainer(value);
    }
    return out;
}
/** A legitimate `null` (confirmed absent) is preserved; a malformed container collapses to a load error — distinct from absent. */
function parseNullableNamespace(raw) {
    const out = {};
    for (const [container, value] of Object.entries(raw)) {
        out[container] = parseNullableContainer(value);
    }
    return out;
}
/** Keep the document links that parse, drop the rest; any drop flags the namespace error. */
function parseDocuments(raw) {
    if (raw == null || typeof raw !== "object")
        return { items: [], hasLoadErrors: true };
    const candidate = raw;
    if (!Array.isArray(candidate.items))
        return { items: [], hasLoadErrors: true };
    const items = [];
    let dropped = false;
    for (const item of candidate.items) {
        const parsed = overviewDocumentSchema.safeParse(item);
        if (parsed.success)
            items.push(parsed.data);
        else
            dropped = true;
    }
    return { items, hasLoadErrors: candidate.hasLoadErrors === true || dropped };
}
/** Strict-first then field-by-field: if the whole thing parses cleanly, return it; otherwise
 *  probe each enriched field and fall back to a neutral default when it drifts, so a single
 *  broken field (e.g. an unknown status string) doesn't disable the chip entirely. */
function parseMetaAwareContainer(raw) {
    const strict = metaAwareContainerSchema.safeParse(raw);
    if (strict.success)
        return strict.data;
    if (raw == null || typeof raw !== "object")
        return FAILED_META_AWARE_CONTAINER;
    const candidate = raw;
    const base = overviewContainerSchema.safeParse({
        data: candidate.data,
        hasLoadError: candidate.hasLoadError,
    });
    const baseData = base.success ? base.data : { data: null, hasLoadError: true };
    const metaStatusParsed = containerMetaStatusSchema.nullable().safeParse(candidate.metaStatus);
    const metaStatus = metaStatusParsed.success ? metaStatusParsed.data : null;
    const metaStatusOk = metaStatusParsed.success;
    const documentCountParsed = z.number().safeParse(candidate.documentCount);
    const documentCount = documentCountParsed.success ? documentCountParsed.data : 0;
    const commentsResult = parseArrayItems(overviewCommentSchema, candidate.comments);
    return {
        ...baseData,
        metaStatus,
        metaHasLoadError: candidate.metaHasLoadError === true || !metaStatusOk,
        documentCount,
        documentCountHasLoadError: candidate.documentCountHasLoadError === true || !documentCountParsed.success,
        comments: commentsResult.items,
        commentsHasLoadError: candidate.commentsHasLoadError === true || commentsResult.hasErrors,
    };
}
function parseRepairServiceContainer(raw) {
    const strict = repairServiceContainerSchema.safeParse(raw);
    if (strict.success)
        return strict.data;
    if (raw == null || typeof raw !== "object")
        return FAILED_REPAIR_SERVICE_CONTAINER;
    const candidate = raw;
    // Mirror `parseMetaAwareContainer`'s handling of the base envelope: an invalid or missing
    // `hasLoadError` flag fails closed to `true` rather than silently reading as healthy.
    const hasLoadErrorParsed = z.boolean().safeParse(candidate.hasLoadError);
    const hasLoadError = hasLoadErrorParsed.success ? hasLoadErrorParsed.data : true;
    const metaStatusParsed = containerMetaStatusSchema.nullable().safeParse(candidate.metaStatus);
    const metaStatus = metaStatusParsed.success ? metaStatusParsed.data : null;
    const metaStatusOk = metaStatusParsed.success;
    const documentCountParsed = z.number().safeParse(candidate.documentCount);
    const documentCount = documentCountParsed.success ? documentCountParsed.data : 0;
    const commentsResult = parseArrayItems(overviewCommentSchema, candidate.comments);
    // Sections is optional at the envelope level (`candidate.sections === undefined` = empty list,
    // not an error), so distinguish "missing" from "invalid": missing → no error, otherwise probe.
    const sectionsResult = candidate.sections === undefined ? { items: [], hasErrors: false } : parseArrayItems(overviewSectionSchema, candidate.sections);
    return {
        hasLoadError,
        metaStatus,
        metaHasLoadError: candidate.metaHasLoadError === true || !metaStatusOk,
        documentCount,
        documentCountHasLoadError: candidate.documentCountHasLoadError === true || !documentCountParsed.success,
        comments: commentsResult.items,
        commentsHasLoadError: candidate.commentsHasLoadError === true || commentsResult.hasErrors,
        sections: sectionsResult.items,
        sectionsHasLoadError: candidate.sectionsHasLoadError === true || sectionsResult.hasErrors,
    };
}
function parseCostEstimateRelevantContainers(raw) {
    return {
        ContainerEstimate: parseMetaAwareContainer(raw.ContainerEstimate),
        ContainerCommissionedExpertReport: parseMetaAwareContainer(raw.ContainerCommissionedExpertReport),
        ContainerExpertReport: parseMetaAwareContainer(raw.ContainerExpertReport),
        ContainerRepairService: parseRepairServiceContainer(raw.ContainerRepairService),
    };
}
function parseDamageAppraisalRelevantContainers(raw) {
    return {
        ContainerCommissionedExpertReport: parseMetaAwareContainer(raw.ContainerCommissionedExpertReport),
        ContainerExpertReport: parseMetaAwareContainer(raw.ContainerExpertReport),
        ContainerRepairService: parseRepairServiceContainer(raw.ContainerRepairService),
    };
}
export function parseCostEstimateIncidentOverview(envelope) {
    return {
        claiminiId: envelope.claiminiId,
        workflow: "cost-estimate",
        overview: parseOverviewNamespace(envelope.overview),
        item: parseNullableNamespace(envelope.item),
        customer: parseNullableNamespace(envelope.customer),
        documents: parseDocuments(envelope.documents),
        workshop: parseNullableNamespace(envelope.workshop),
        relevantContainers: parseCostEstimateRelevantContainers(envelope.relevantContainers),
    };
}
export function parseDamageAppraisalIncidentOverview(envelope) {
    return {
        claiminiId: envelope.claiminiId,
        workflow: "damage-appraisal",
        overview: parseOverviewNamespace(envelope.overview),
        item: parseNullableNamespace(envelope.item),
        customer: parseNullableNamespace(envelope.customer),
        documents: parseDocuments(envelope.documents),
        relevantContainers: parseDamageAppraisalRelevantContainers(envelope.relevantContainers),
    };
}
// The open namespaces are projected through explicit name→role maps: every role a map lists is
// owned by the workflow (see ./contract.ts) and always gets a key, `null` or not; unknown wire
// containers are dropped. The two enriched containers damage-appraisal also delivers plain
// (`ContainerEstimate`/`ContainerCommissionedExpertReport`) are deliberately not in this map —
// they project onto their meta roles below, not onto roles of their own.
const OVERVIEW_NAMESPACE_ROLES = {
    ContainerIncident: "incident",
    ContainerAutomaticDispatches: "automaticDispatches",
    ContainerPersonType: "personType",
    ContainerEventLocation: "eventLocation",
    ContainerRepairProcedure: "repairProcedure",
    ContainerDamage: "damage",
    ContainerLawyer: "lawyer",
};
const ITEM_NAMESPACE_ROLES = {
    ContainerItemData: "itemData",
    ContainerInsurance: "insurance",
    ContainerLeasing: "leasing",
    ContainerLinkItemIncident: "linkedIncidents",
};
const CUSTOMER_NAMESPACE_ROLES = {
    ContainerPersonComplete: "personComplete",
};
// Cost-estimate's workshop namespace — the garage's address/contact and dispatch conditions.
// Damage-appraisal carries no workshop namespace.
const WORKSHOP_NAMESPACE_ROLES = {
    ContainerAddressAndContactDetails: "garageAddress",
    ContainerAutomaticDispatches: "garageDispatches",
};
/** Maps each name→role–listed container, folded to its tagged view shape. Every role the map
 *  lists is owned (see ./contract.ts), so it always gets a key: `null` when the wire entry is
 *  absent or confirmed-absent, the folded container otherwise. */
function projectNamespace(namespace, roles) {
    const containers = {};
    for (const [containerName, role] of Object.entries(roles)) {
        const container = namespace[containerName];
        containers[role] = container == null ? null : toContainer(container);
    }
    return containers;
}
/**
 * Reconciles a container the wire delivers through two channels — a plain copy and an enriched
 * copy of the same read — into its single role: the enriched channel wins while healthy, an
 * intact plain copy backfills `data` when it doesn't, and `hasLoadError` is set only when every
 * channel failed. `meta` always comes from the enriched channel, keeping its own failure flags.
 */
function mergeChannels(plain, enriched) {
    const meta = metaOf(enriched);
    const winner = !enriched.hasLoadError || plain == null || plain.hasLoadError ? enriched : plain;
    return { content: toContainer(winner).content, meta };
}
export function projectCostEstimateOverview(overview) {
    const containers = {
        ...projectNamespace(overview.overview, OVERVIEW_NAMESPACE_ROLES),
        ...projectNamespace(overview.item, ITEM_NAMESPACE_ROLES),
        ...projectNamespace(overview.customer, CUSTOMER_NAMESPACE_ROLES),
        ...projectNamespace(overview.workshop, WORKSHOP_NAMESPACE_ROLES),
        estimate: toMetaContainer(overview.relevantContainers.ContainerEstimate),
        commissionedExpertReport: toMetaContainer(overview.relevantContainers.ContainerCommissionedExpertReport),
        expertReport: toMetaContainer(overview.relevantContainers.ContainerExpertReport),
    };
    return {
        claiminiId: overview.claiminiId ?? null,
        containers,
        sections: { repairRelease: toSectionsContainer(overview.relevantContainers.ContainerRepairService) },
        documents: overview.documents,
    };
}
export function projectDamageAppraisalOverview(overview) {
    // The estimate arrives plain-channel-only — no enriched delivery, so no meta facet.
    const rawEstimate = overview.overview.ContainerEstimate;
    const containers = {
        ...projectNamespace(overview.overview, OVERVIEW_NAMESPACE_ROLES),
        ...projectNamespace(overview.item, ITEM_NAMESPACE_ROLES),
        ...projectNamespace(overview.customer, CUSTOMER_NAMESPACE_ROLES),
        // Delivered both plain and enriched (the same underlying read, serialized twice).
        commissionedExpertReport: mergeChannels(overview.overview.ContainerCommissionedExpertReport, overview.relevantContainers.ContainerCommissionedExpertReport),
        expertReport: toMetaContainer(overview.relevantContainers.ContainerExpertReport),
        estimate: rawEstimate == null ? null : toContainer(rawEstimate),
    };
    return {
        claiminiId: overview.claiminiId ?? null,
        containers,
        sections: { repairRelease: toSectionsContainer(overview.relevantContainers.ContainerRepairService) },
        documents: overview.documents,
    };
}
